import { useCallback, useEffect, useMemo, useState } from 'react'

import { useRouter } from 'next/navigation'
import { useIntl } from 'react-intl'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type GlobalHeaderProps,
  type NotificationDropdownItem,
  NotificationsDropdown,
} from '@b-stock/fe-core/bstock-react'
import type { NotificationReadReceiptItemDto } from '@b-stock/notification-api-client'

import { FeatureFlags } from '@/helpers/featureFlags'
import { appendClickSourceParams } from '@/helpers/notificationClickSource'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useNotificationRealtimeUpdates } from '@/helpers/useNotificationRealtimeUpdates'
import { useNotificationList } from '@/queries/notificationListQuery'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationReadOnNavigate,
} from '@/queries/notificationReadReceiptMutations'
import { useNotificationUnreadCount } from '@/queries/notificationUnreadCountQuery'

import { buildOverlayCandidate } from '../NotificationOverlay/buildOverlayCandidate'
import NotificationOverlay from '../NotificationOverlay/NotificationOverlay'
import type { NotificationOverlayNotification } from '../NotificationOverlay/types'
import { useNotificationOverlayPolicy } from '../NotificationOverlay/useNotificationOverlayPolicy'
import {
  type OpenDropdownState,
  useOpenDropdownToggle,
} from './useOpenDropdownState'

// `notificationType` rides along so downstream analysis can break down
// click-through by type without re-deriving it from the destination route.
const NOTIFICATION_CLICK_SOURCE = 'notification_tray'

// Notification types whose notification-service template doesn't currently
// set `content.deepLink` (confirmed against the real tray: Saved Search,
// Order Created/Updated, Bid Placed, Order Updated all arrive with no deep
// link today). Until notification-service's templates are updated to set
// one, these fall back to the relevant list page instead of rendering as a
// dead, non-interactive row - confirmed against the design prototype, which
// sends order-lifecycle types to the Orders list (not a specific order's
// detail page) and bid-lifecycle types to the Bids list (not the listing
// page). A real `deepLink` from the backend always wins over this fallback.
type FallbackSection = 'orders' | 'bids' | 'watchlist' | 'account'

const FALLBACK_HREF_TYPE_SECTION: Readonly<Record<string, FallbackSection>> = {
  ORDER_CREATED: 'orders',
  ORDER_UPDATED: 'orders',
  ORDER_CLOSED: 'orders',
  ORDER_SHIPPED: 'orders',
  SHIPMENT_DELIVERED: 'orders',
  TRACKING_INFO_UPDATED: 'orders',
  ORDER_PAYMENT_REMINDER: 'orders',
  PAYMENT_FAILED_BUYER_NOTIFICATION: 'orders',
  ORDER_PAID: 'orders',
  SELLER_COLLECTS_PAYMENT_DUE: 'orders',
  BUYER_ORDER_PICKED_UP: 'orders',
  BID_PLACED: 'bids',
  BID_WON: 'bids',
  BID_OUTBID: 'bids',
  WATCHLIST_CLOSING_SOON: 'watchlist',
  ACCOUNT_MERGED: 'account',
}

// These two notification-service type families ship new suffixed variants
// per seller/program (e.g. `ORDER_CREATED_SELLER_COLLECTS_WHIRLPOOL_DD`) or
// per flow (e.g. `BUYER_SCHEDULE_SHIPPING_TARGET`) without a corresponding
// frontend change - matched by prefix instead of an exact-match entry per
// variant, so a newly-added variant automatically gets the same fallback
// section instead of silently rendering as a dead row until someone notices
// and files another ticket (see BP-3490).
const FALLBACK_HREF_TYPE_PREFIX_SECTION: ReadonlyArray<{
  prefix: string
  section: FallbackSection
}> = [
  { prefix: 'ORDER_CREATED_SELLER_COLLECTS', section: 'orders' },
  { prefix: 'BUYER_SCHEDULE_SHIPPING', section: 'orders' },
]

const resolveFallbackSection = (
  notificationType: string
): FallbackSection | undefined =>
  FALLBACK_HREF_TYPE_SECTION[notificationType] ??
  FALLBACK_HREF_TYPE_PREFIX_SECTION.find(({ prefix }) =>
    notificationType.startsWith(prefix)
  )?.section

// Exported for direct unit testing - the real personId comes from a
// decoded-access-token hook that isn't easily overridden per-case in
// GlobalHeaderWrapper's existing JWT-fixture-based test setup.
export const buildFallbackHref = (
  section: FallbackSection,
  { accountId, personId }: { accountId: string; personId: string | undefined }
): string => {
  switch (section) {
    case 'orders':
      return `/buy/user/${accountId}/orders`
    case 'bids':
      return `/buy/user/${accountId}/bids`
    case 'account':
      return `/buy/my-account/${accountId}/personal`
    case 'watchlist':
      // Watchlist is addressed by personId, not accountId, unlike every
      // other fallback destination. `/buy/p/[personId]/watchlist` already
      // special-cases the literal segment 'my' and redirects to the real
      // personId from the access token server-side, so that's used instead
      // of personId whenever it hasn't loaded client-side yet, rather than
      // suppressing the link entirely.
      return `/buy/p/${personId ?? 'my'}/watchlist`
    default: {
      const unhandled: never = section
      throw new Error(`Unhandled fallback section: ${String(unhandled)}`)
    }
  }
}

// Every tray-lifecycle analytics event describes something happening on this
// one surface - factored out so the three call sites below can't drift from
// each other on the literal.
const NOTIFICATION_SURFACE = 'tray'
// The tray only ever deals in in-app delivery - email/SMS have no tray
// equivalent - so every tray-lifecycle event reports this same channel.
const NOTIFICATION_CHANNEL = 'IN_APP'

// Separate surface literal for the overlay's own lifecycle events, so they
// can't be mistaken for the tray's.
const NOTIFICATION_OVERLAY_SURFACE = 'overlay'

// Candidates accumulate from real-time Pusher events for as long as the
// buyer's session is open - capped so a long session doesn't grow this list
// unboundedly. Small on purpose: the policy hook only ever needs "is there
// something newer/eligible than what's currently shown," not a full history.
const OVERLAY_CANDIDATE_LIMIT = 5

export function useNotifications({
  accountId,
  personId,
  isAuthenticated,
  openDropdownState,
}: {
  accountId: string | undefined
  personId: string | undefined
  isAuthenticated: boolean
  openDropdownState: OpenDropdownState
}) {
  const router = useRouter()
  const { formatMessage } = useIntl()
  const { track, trackButtonClicked } = useAnalytics()
  const isNotificationCenterEnabled = useFlag(
    FeatureFlags.ENABLE_NOTIFICATION_CENTER
  )

  const { onOpenChange, isOpen } = useOpenDropdownToggle({
    key: 'notifications',
    openDropdownState,
  })

  const isNotificationFeatureEnabled =
    isNotificationCenterEnabled && isAuthenticated && !!accountId

  const { data: unreadCountData } = useNotificationUnreadCount({
    enabled: isNotificationFeatureEnabled,
  })

  // Only fetch the list once the tray is opened; the badge count above
  // stays warm on every page regardless.
  const {
    data: notificationPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingNotifications,
    isError: isNotificationListError,
  } = useNotificationList({
    enabled: isNotificationFeatureEnabled && isOpen,
  })

  const handleNotificationDelivered = useCallback(
    (
      notificationId: string | undefined,
      notificationType: string | undefined,
      sourceNotificationId: string
    ) => {
      track('notification_delivered', {
        notification_id: notificationId,
        notification_type: notificationType,
        surface: NOTIFICATION_SURFACE,
        channel: NOTIFICATION_CHANNEL,
        // notification-service's own id for the underlying Notification -
        // always present, even when notification_id above is undefined
        // (the receipt lookup failed/missed) - never conflated with
        // notification_id itself, since the two are different kinds of id.
        source_notification_id: sourceNotificationId,
      })
    },
    [track]
  )

  // Priority order: most-recently-received first. useNotificationOverlayPolicy
  // reads this as "candidates in priority order; the first eligible one
  // wins," so a newer notification naturally outranks an older one.
  const [overlayCandidates, setOverlayCandidates] = useState<
    NotificationOverlayNotification[]
  >([])

  // Cleared on every accountId change (login, logout, or switching accounts
  // without a full remount) so a candidate built for the previous account
  // can never linger into the new one, even though in practice globally
  // -unique receipt ids make an actual collision unlikely.
  useEffect(() => {
    setOverlayCandidates([]) // oxlint-disable-line react/set-state-in-effect
  }, [accountId])

  const handleNotificationReceived = useCallback(
    (receipt: NotificationReadReceiptItemDto | undefined) => {
      // No receipt means the lookup failed/missed - nothing to add as a
      // candidate (unlike notification_delivered, there's no "unknown
      // candidate" concept the overlay could show).
      if (!receipt) return
      const candidate = buildOverlayCandidate(receipt, formatMessage)
      setOverlayCandidates((previous) => {
        if (previous.some((existing) => existing.id === candidate.id)) {
          return previous
        }
        return [candidate, ...previous].slice(0, OVERLAY_CANDIDATE_LIMIT)
      })
    },
    [formatMessage]
  )

  useNotificationRealtimeUpdates({
    enabled: isNotificationFeatureEnabled,
    channelName: unreadCountData?.pusherChannelName,
    onNotificationDelivered: handleNotificationDelivered,
    onNotificationReceived: handleNotificationReceived,
  })

  const handleOverlayShown = useCallback(
    (notification: NotificationOverlayNotification) => {
      track('notification_overlay_shown', {
        notification_id: notification.id,
        notification_type: notification.notificationType,
        surface: NOTIFICATION_OVERLAY_SURFACE,
        channel: NOTIFICATION_CHANNEL,
        destination: notification.action?.href,
      })
    },
    [track]
  )

  const handleOverlayDismissed = useCallback(
    (notification: NotificationOverlayNotification) => {
      track('notification_overlay_dismissed', {
        notification_id: notification.id,
        notification_type: notification.notificationType,
        surface: NOTIFICATION_OVERLAY_SURFACE,
        channel: NOTIFICATION_CHANNEL,
      })
    },
    [track]
  )

  const selectedOverlayNotification = useNotificationOverlayPolicy(
    overlayCandidates,
    handleOverlayShown
  )

  const notificationOverlay = (
    <NotificationOverlay
      notification={selectedOverlayNotification}
      onDismiss={handleOverlayDismissed}
    />
  )

  const markNotificationReadOnNavigate = useMarkNotificationReadOnNavigate()
  const markAllNotificationsRead = useMarkAllNotificationsRead()

  // isError is true for any failed fetch, including a fetchNextPage retry —
  // only treat it as a full-panel error when there are no items loaded yet,
  // so a failed "Load more" doesn't hide notifications already on screen.
  const hasNoItemsYet = !notificationPages
  const showFullPanelError = isNotificationListError && hasNoItemsYet

  const items: NotificationDropdownItem[] = useMemo(() => {
    const flattened =
      notificationPages?.pages.flatMap((page) =>
        page.receipts.map((receipt) => ({
          id: receipt._id,
          notificationType: receipt.content.notificationType,
          createdAt: receipt.createdAt,
          read: receipt.read,
          jsonPayload: receipt.content.jsonPayload as
            | Record<string, unknown>
            | undefined,
          deepLink: receipt.content.deepLink,
        }))
      ) ?? []

    // Offset-based paging over a live feed can return the same item twice:
    // if a new notification arrives between page fetches, every existing
    // item shifts down one position and the next page re-fetches the last
    // item of the previous page. Dedupe by id defensively.
    const seenIds = new Set<string>()
    const deduped = flattened.filter((item) => {
      if (seenIds.has(item.id)) return false
      seenIds.add(item.id)
      return true
    })

    return deduped.map(({ deepLink, ...item }, index) => {
      const fallbackSection = resolveFallbackSection(item.notificationType)
      const rawDestination =
        deepLink ||
        (accountId && fallbackSection
          ? buildFallbackHref(fallbackSection, { accountId, personId })
          : undefined)
      const href = rawDestination
        ? appendClickSourceParams(
            rawDestination,
            item.notificationType,
            NOTIFICATION_CLICK_SOURCE
          )
        : undefined
      // No destination to send the buyer to - either missing, or malformed
      // beyond repair - leave both href and onClick unset so fe-core renders
      // a non-interactive row instead of a dead/broken link.
      if (!href) return item

      return {
        ...item,
        href,
        onClick: () => {
          // Already-read items have nothing to mark; skip the request
          // entirely rather than sending a no-op PATCH on every click.
          if (!item.read) {
            // Fire-and-forget, matching useWatchlist/useSavedSearch: the
            // mark-read call shouldn't delay or block the href's own
            // navigation. See useMarkNotificationReadOnNavigate's doc comment
            // for why this specific path needs a synchronous dispatch.
            markNotificationReadOnNavigate(item.id)
          }
          // Pre-existing minimal click signal, kept as-is for dashboard
          // parity with the sibling watchlist/saved-search dropdowns.
          trackButtonClicked({
            buttonName: 'global_header_notification_clicked',
            notification_id: item.id,
            notification_type: item.notificationType,
            position_in_tray: index + 1,
          })
          // The richer, notification-center-specific click event - additive
          // to the minimal signal above, not a replacement for it.
          track('notification_clicked', {
            notification_id: item.id,
            notification_type: item.notificationType,
            surface: NOTIFICATION_SURFACE,
            channel: NOTIFICATION_CHANNEL,
            position_in_tray: index + 1,
            destination: href,
          })
        },
      }
    })
  }, [
    notificationPages,
    accountId,
    personId,
    markNotificationReadOnNavigate,
    trackButtonClicked,
    track,
  ])

  // Only track opens, not closes - "opened" is the meaningful lifecycle
  // moment Data Eng wants; a close is just the open toggling back off and
  // isn't one of this ticket's three events.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange(open)
      if (open) {
        track('notification_tray_opened', {
          surface: NOTIFICATION_SURFACE,
          channel: NOTIFICATION_CHANNEL,
          unread_count: unreadCountData?.count,
        })
      }
    },
    [onOpenChange, track, unreadCountData?.count]
  )

  const handleEditPreferences = useCallback(() => {
    onOpenChange(false)
    router.push(`/buy/my-account/${accountId}/notification-preferences`)
  }, [router, accountId, onOpenChange])

  const handleLoadMore = useCallback(() => {
    void fetchNextPage()
  }, [fetchNextPage])

  // `.mutate` is referentially stable across renders; the mutation result
  // object itself isn't (it changes identity with every state update), so
  // depending on it directly would keep this (and the memos chained off it)
  // recomputing on every mutation state change.
  /* oxlint-disable eslint-plugin-react-hooks/exhaustive-deps */
  const handleMarkAllRead = useCallback(() => {
    markAllNotificationsRead.mutate()
  }, [markAllNotificationsRead.mutate])
  /* oxlint-enable eslint-plugin-react-hooks/exhaustive-deps */

  const content = useMemo(
    () =>
      isAuthenticated && accountId ? (
        <NotificationsDropdown
          items={items}
          isLoading={isLoadingNotifications}
          isError={showFullPanelError}
          hasMore={hasNextPage}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={handleLoadMore}
          onEditPreferences={handleEditPreferences}
          onMarkAllRead={handleMarkAllRead}
        />
      ) : null,
    [
      isAuthenticated,
      accountId,
      items,
      isLoadingNotifications,
      showFullPanelError,
      hasNextPage,
      isFetchingNextPage,
      handleLoadMore,
      handleEditPreferences,
      handleMarkAllRead,
    ]
  )

  // Buyer-account feature — only surface the icon to authenticated users
  // (matches useWatchlist/useSavedSearch), and only when the flag is on so
  // flag-off stays byte-identical to today's disabled slot.
  const notificationsSlot = useMemo(
    () =>
      isNotificationCenterEnabled && content
        ? {
            content,
            onOpenChange: handleOpenChange,
            isOpen,
            badgeCount: unreadCountData?.count,
          }
        : undefined,
    [
      isNotificationCenterEnabled,
      content,
      handleOpenChange,
      isOpen,
      unreadCountData?.count,
    ]
  )

  return {
    notificationsSlot,
    notificationOverlay,
  } satisfies Pick<GlobalHeaderProps, 'notificationsSlot'> & {
    notificationOverlay: typeof notificationOverlay
  }
}
